import { Component, ComponentType, FC, memo } from 'react'
import { Typography, Grid, styled, colors, Stack, Box } from '@mui/material'
import { DataGrid, DataGridProps, GridColDef, GridRowIdGetter, GridValidRowModel, GridRowSelectionModel } from '@mui/x-data-grid'
import { GridColsDef, IViewModeListConfig, IViewModeModuleConfig, IViewModeValidModel } from './view-mode.types'

//#region Mode Module
const EmptyModule: FC = () => (
  <Typography variant='subtitle1' color='textSecondary'>
    No items to display
  </Typography>
)

export interface IViewModeModuleProps<T extends IViewModeValidModel> {
  data: T[]
  configs: IViewModeModuleConfig<T>
  autoHeight?: boolean
}

export function createViewModeModule<T extends IViewModeValidModel>(): ComponentType<IViewModeModuleProps<T>> {
  class ViewModeModule extends Component<IViewModeModuleProps<T>> {
    private ElementComponent
    constructor(props: IViewModeModuleProps<T>) {
      super(props)
      this.ElementComponent = props.configs.Element || EmptyModule
    }

    shouldComponentUpdate(nextProps: Readonly<IViewModeModuleProps<T>>): boolean {
      const { Element: currentElement, ...currentConfig } = this.props.configs
      const { Element: nextElement, ...nextConfigs } = nextProps.configs
      const checkElementDiff = currentElement !== nextElement
      if (checkElementDiff) {
        this.ElementComponent = nextProps.configs.Element || EmptyModule
      }

      const checkConfigsDiff = JSON.stringify(currentConfig) !== JSON.stringify(nextConfigs)
      const checkDataDiff = JSON.stringify(this.props.data) !== JSON.stringify(nextProps.data)
      return checkDataDiff || checkConfigsDiff
    }

    render() {
      const { ElementComponent } = this
      const { data, configs } = this.props
      const size = configs.size || { xs: 12, sm: 6, md: 4, lg: 3 }

      return (
        <ViewModeModuleStyled className={this.props.autoHeight ? 'auto-height' : ''}>
          <Grid container spacing={configs.spacing || 0} {...configs.wrapProps} style={{ height: '100%' }}>
            {data.map((item, index) => {
              const key = configs.getElementId ? configs.getElementId(item) : index.toString()
              const getterFunc = configs.elementWrapPropsGetter
              const itemProps = getterFunc ? getterFunc({ data: item, index }) : configs.elementWrapProps || {}
              return (
                <Grid item key={key} {...size} {...itemProps}>
                  <ElementComponent data={item} index={index} />
                </Grid>
              )
            })}
          </Grid>
        </ViewModeModuleStyled>
      )
    }
  }

  return memo(ViewModeModule)
}

const ViewModeModuleStyled = styled(Box)({
  height: '100%',
  width: '100%',
  overflow: 'auto',
  paddingBottom: '16px',
  '&.auto-height': {
    height: 'auto',
    overflow: 'visible'
  }
})
//#endregion

//#region View Mode List
export interface IViewModeListProps<T extends IViewModeValidModel> {
  value: T[]
  configs: IViewModeListConfig<T>
  autoHeight?: boolean
  slots?: {
    dataGridProps?: Partial<Omit<DataGridProps, 'columns' | 'rows'>>
  }
}

export function createViewModeList<T extends IViewModeValidModel>(): ComponentType<IViewModeListProps<T>> {
  const genarateColumns = (configs: IViewModeListConfig<T>): GridColDef[] => {
    const cols: GridColsDef<T> = configs?.columns ?? {}
    const fields = Object.keys(cols) as (keyof T)[]
    return fields.map((field) => {
      const config = cols[field]
      return { field: String(field), sortable: false, ...config }
    })
  }

  class ViewModeList extends Component<IViewModeListProps<T>> {
    private columns: GridColDef[] = []
    private internalRowSelectionModel: GridRowSelectionModel = []

    constructor(props: IViewModeListProps<T>) {
      super(props)
      this.columns = genarateColumns(props.configs)
      // Initialize internal state from props if provided
      if (props.configs?.rowSelectionModel) {
        this.internalRowSelectionModel = props.configs.rowSelectionModel
      }
    }

    private handleRowSelectionChange = (newSelection: GridRowSelectionModel) => {
      // Only update internal state if not controlled from outside
      if (this.props.configs?.rowSelectionModel === undefined) {
        this.internalRowSelectionModel = newSelection
      }
      this.props.configs?.onRowSelectionChange?.(newSelection as (string | number)[])
      this.forceUpdate()
    }

    shouldComponentUpdate(nextProps: Readonly<IViewModeListProps<T>>): boolean {
      const checkValueDiff = JSON.stringify(this.props.value) !== JSON.stringify(nextProps.value)
      const checkListConfigsDiff = JSON.stringify(this.props.configs) !== JSON.stringify(nextProps.configs)
      if (checkListConfigsDiff) {
        this.columns = genarateColumns(nextProps.configs)
      }
      // Reset selection when data changed to avoid potential selection mismatch (only if uncontrolled)
      if (checkValueDiff && nextProps.configs?.rowSelectionModel === undefined) {
        this.internalRowSelectionModel = []
      }
      // Sync internal state with external prop if controlled
      if (nextProps.configs?.rowSelectionModel !== undefined) {
        this.internalRowSelectionModel = nextProps.configs.rowSelectionModel
      }
      return checkValueDiff || checkListConfigsDiff
    }

    render() {
      const isRender = this.props.configs && Object.keys(this.props.configs?.columns ?? {}).length > 0
      if (!isRender) return null
      const rows = this.props.value as GridValidRowModel[]
      // Use external prop if provided (controlled), otherwise use internal state (uncontrolled)
      const currentSelection = this.props.configs?.rowSelectionModel ?? this.internalRowSelectionModel
      return (
        <Stack sx={{ flex: 1, width: '100%', height: '100%', minHeight: '100px' }}>
          <DataGridCustom
            hideFooter
            // disableColumnSorting
            disableColumnFilter
            disableColumnMenu
            checkboxSelection={this.props.configs?.enableRowSelection ?? false}
            rowSelectionModel={currentSelection}
            onRowSelectionModelChange={this.handleRowSelectionChange}
            getRowId={this.props.configs?.getRowId as GridRowIdGetter<GridValidRowModel>}
            rows={rows}
            rowHeight={this.props.configs?.rowHeight}
            columnHeaderHeight={48}
            getRowSpacing={({ isFirstVisible }) => ({ top: isFirstVisible ? 8 : 4, bottom: 4 })}
            columns={this.columns}
            disableColumnSelector
            autoHeight={this.props.autoHeight}
            {...this.props.slots?.dataGridProps}
          />
        </Stack>
      )
    }
  }

  return memo(ViewModeList)
}

const DataGridCustom = styled(DataGrid, { shouldForwardProp: (prop) => prop !== 'autoHeight' })<{
  autoHeight?: boolean
}>((params: any) => {
  const { theme, ownerState } = params as any
  const autoHeight = ownerState?.autoHeight
  return {
    // When `autoHeight` is true let the grid size to its content, otherwise fill parent.
    height: autoHeight ? 'auto' : '100%',
    width: '100%',
    display: autoHeight ? 'block' : 'flex',
    flexDirection: autoHeight ? undefined : 'column',
    '*': { boxSizing: 'border-box' },
    '--custom-border-color': 'rgba(145, 158, 171, 0.16)',
    '--custom-bg-color': theme.palette.background.paper,
    '--DataGrid-rowBorderColor': 'transparent',
    borderColor: 'transparent',
    '.MuiDataGrid-cell': { '--rowBorderColor': 'transparent', display: 'flex', alignItems: 'center' },
    '.MuiDataGrid-cell:focus, .MuiDataGrid-columnHeader:focus': { outline: 'none' },
    '.MuiDataGrid-cell:focus-within, .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
    '.MuiDataGrid-cell.Mui-focusVisible, .MuiDataGrid-columnHeader.Mui-focusVisible': { outline: 'none' },
    '.MuiDataGrid-columnHeaders': { backgroundColor: colors.grey[50], borderColor: 'transparent', borderRadius: '6px' },
    '.MuiDataGrid-columnHeaderCheckbox': { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    '.MuiDataGrid-cellCheckbox': { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    '.MuiDataGrid-virtualScroller': {
      paddingBottom: '16px',
      ...(autoHeight ? { minHeight: 0 } : { flex: 1, minHeight: 0 })
    },
    '.MuiDataGrid-virtualScrollerContent': {
      '.MuiDataGrid-row': {
        borderRadius: '6px',
        border: 'none',
        transition: 'all 0.2s ease',
        backgroundColor: 'var(--custom-bg-color)',
        '.MuiDataGrid-cell': {
          backgroundColor: 'var(--custom-bg-color)',
          border: 'none',
          borderTop: '1px dashed var(--custom-border-color)',
          borderBottom: '1px dashed var(--custom-border-color)',
          '&:first-of-type': { borderLeft: '1px dashed var(--custom-border-color)', borderRadius: '6px 0 0 6px' },
          '&:last-of-type': { borderRight: '1px dashed var(--custom-border-color)', borderRadius: '0 6px 6px 0' }
        },
        '&:hover': {
          '--custom-border-color': theme.palette.divider,
          '--custom-bg-color': colors.grey[50],
          transform: 'translateY(-1px)',
          boxShadow: 'rgba(99, 99, 99, 0.1) 0px 2px 8px 0px',
          '.MuiDataGrid-cell': {
            borderTopStyle: 'solid',
            borderBottomStyle: 'solid',
            '&:first-of-type': { borderLeftStyle: 'solid' },
            '&:last-of-type': { borderRightStyle: 'solid' }
          }
        }
      }
    }
  }
})
//#endregion
